import type { BatteryLevel } from '../entities/battery-level';
import type { AllIdFilters } from '../entities/filters';

/**
 * Determines the parent ID for a given level based on the current filters
 * Extracted from level-filters.tsx for reuse across components
 */
export function getParentIdForLevel(
  level: BatteryLevel,
  levelsOrder: BatteryLevel[],
  filters: AllIdFilters,
  assetId: string,
): string | undefined {
  const idx = levelsOrder.indexOf(level);

  if (idx === 0) {
    return assetId;
  }

  if (idx > 0) {
    const parentLevel = levelsOrder[idx - 1];
    const parentValue = filters[`${parentLevel}Id`];

    // Handle both single and array values - use first value if array
    return Array.isArray(parentValue) ? parentValue[0] : parentValue;
  }
  return undefined;
}

/**
 * Determines the current active level and its parent ID from filters
 * Used to know which ListIds query to make for getting nodes data
 */
export function getCurrentLevelInfo(
  filters: AllIdFilters,
  levelsOrder: BatteryLevel[],
  assetId: string,
): { level: BatteryLevel; parentId: string | undefined } | null {
  // Check levels in reverse hierarchy order (deepest first) to find the current selection
  const reversedLevels = [...levelsOrder].reverse();

  for (const level of reversedLevels) {
    const filterKey = `${level}Id` as const;
    const filterValue = filters[filterKey];

    if (filterValue) {
      // Found the current level, now get its parent ID
      const parentId = getParentIdForLevel(
        level,
        levelsOrder,
        filters,
        assetId,
      );
      return { level, parentId };
    }
  }

  return null;
}
